import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { useWorkflowFieldValues } from 'features/libra/fieldValues/useWorkflowFieldValues'
import {
  FieldValues,
  OntologyField,
} from 'features/libra/ontology/ontologyFieldTypes'
import { useTicketOntologyFields } from 'features/libra/ontology/useOntologyFields'
import { SaveGuardModal } from 'features/libra/SaveGuardModal'
import { isComputedSection } from 'features/libra/sections/computedSection/computedSectionTypes'
import { resolveFormLayout } from 'features/libra/sections/formLayout'
import { FormSection } from 'features/libra/sections/formSection/formSectionTypes'
import { applyAiFieldValues } from 'features/libra/stepComponents/applyAiFieldValues'
import { useActiveStepId } from 'features/libra/ticket/TicketStateContext'
import { useRegisterSaveGuard } from 'features/libra/useRegisterSaveGuard'
import {
  LibraWorflowStepComponentProps,
  UseValidateConfigHook,
} from 'features/libra/workflow/workflowStepsApi'
import type { Dispatch, FC, SetStateAction } from 'react'
import React, { useCallback, useMemo } from 'react'
import styled from 'styled-components'

import { ComputedSectionView } from './conditionalForm/computed/ComputedSectionView'
import { fieldLabelsForIds } from './fieldLabelsForIds'
import { AiAutofillBanner } from './GongAgentAutofill'
import {
  NiiSaveBanner,
  StickySaveBannerContainer,
} from './niiReport/NiiSaveBanner'
import { NiiSeverityAndRiskCard } from './niiScreening/cards/NiiSeverityAndRiskCard'
import { NiiSuitabilityCard } from './niiScreening/cards/NiiSuitabilityCard'
import {
  NiiScreeningConfig,
  niiScreeningConfigSchema,
  NiiScreeningSection,
} from './niiScreening/niiScreeningApi'
import {
  NiiScreeningDataProvider,
  useNiiScreeningData,
} from './niiScreening/NiiScreeningDataContext'
import { NiiScreeningFormSection } from './niiScreening/NiiScreeningFormSection'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
`

/**
 * Legacy content component - used when no sections are configured
 * Renders the hardcoded card layout for backwards compatibility
 */
const LegacyNiiScreeningContent: FC<{ readonly?: boolean }> = ({
  readonly,
}) => {
  const { hasChanges, resetChanges, doSave, isLoading } = useNiiScreeningData()

  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits: !readonly && hasChanges,
    save: doSave,
  })

  return (
    <Container>
      <SaveGuardModal {...saveGuard} />
      {!readonly && (
        <StickySaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasChanges}
            isLoading={isLoading}
            onReset={resetChanges}
            onSave={doSave}
          />
        </StickySaveBannerContainer>
      )}
      <NiiSuitabilityCard readonly={readonly} />
      <NiiSeverityAndRiskCard readonly={readonly} />
      {/* NiiScreeningOutcome removed - use config-driven approach instead */}
    </Container>
  )
}

interface SectionRendererProps {
  section: NiiScreeningSection
  stepId: string
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  setFieldValues: Dispatch<SetStateAction<FieldValues>>
  sections: NiiScreeningSection[]
  readonly?: boolean
}

/**
 * Form section with an AI autofill banner when the section has an agent config block.
 */
const AgentAwareFormSection: FC<{
  section: FormSection
  stepId: string
  fieldValues: FieldValues
  ontologyFields: Record<string, OntologyField>
  setFieldValues: Dispatch<SetStateAction<FieldValues>>
  readonly?: boolean
}> = ({
  section,
  stepId,
  fieldValues,
  ontologyFields,
  setFieldValues,
  readonly,
}) => {
  const onFieldValues = useCallback(
    (fv: FieldValues) => setFieldValues((prev) => applyAiFieldValues(prev, fv)),
    [setFieldValues]
  )

  return (
    <>
      {!readonly && (
        <AiAutofillBanner
          section={section}
          fieldValues={fieldValues}
          fieldLabels={fieldLabelsForIds(section.fields, ontologyFields)}
          onFieldValues={onFieldValues}
        />
      )}
      <NiiScreeningFormSection
        config={section}
        ontologyFields={ontologyFields}
        fieldValues={fieldValues}
        setFieldValues={setFieldValues}
        disabled={readonly}
        stepId={stepId}
      />
    </>
  )
}

/**
 * Renders a single section based on its type (computed or form).
 * Form sections with an agent block get an AI autofill banner.
 */
const SectionRenderer: FC<SectionRendererProps> = ({
  section,
  stepId,
  fieldValues,
  ontologyFields,
  setFieldValues,
  sections,
  readonly,
}) => {
  if (isComputedSection(section)) {
    return (
      <CommentIndicator
        anchor={{ type: 'computed', stepId, sectionId: section.id }}>
        <ComputedSectionView
          config={section}
          fieldValues={fieldValues}
          ontologyFields={ontologyFields}
          setFieldValues={setFieldValues}
          context={{
            ontologyFields,
            sections,
          }}
          readonly={readonly}
        />
      </CommentIndicator>
    )
  }

  // Form sections with an agent block get AI autofill
  if ('agent' in section && section.agent) {
    return (
      <AgentAwareFormSection
        section={section}
        stepId={stepId}
        fieldValues={fieldValues}
        ontologyFields={ontologyFields}
        setFieldValues={setFieldValues}
        readonly={readonly}
      />
    )
  }

  return (
    <CommentIndicator
      anchor={{ type: 'section', stepId, sectionId: section.id }}>
      <NiiScreeningFormSection
        config={section}
        ontologyFields={ontologyFields}
        fieldValues={fieldValues}
        setFieldValues={setFieldValues}
        disabled={readonly}
        stepId={stepId}
      />
    </CommentIndicator>
  )
}

/**
 * Config-driven content component - used when sections are configured
 * Renders sections based on the workflow JSON config
 */
const ConfigDrivenNiiScreeningContent: FC<{
  config: NiiScreeningConfig
  readonly?: boolean
}> = ({ config, readonly }) => {
  const stepId = useActiveStepId()
  // NII screening is a ticket-scope step (no per-component fields) — read
  // the top-level ontology only. If a future workflow gives this step
  // component-scoped fields, switch to the scope-aware pattern used in
  // `ConditionalForm`.
  const { ontologyFields } = useTicketOntologyFields()

  const sections = useMemo(() => config.sections ?? [], [config.sections])
  const form = useMemo(
    () => resolveFormLayout(sections, config.form),
    [config.form, sections]
  )

  const {
    resolvedFieldValues,
    setFieldValues,
    hasUnsavedEditsOrSystemValues,
    hasUnsavedEdits,
    handleCancel,
    handleSave,
    canProceed,
    isSavingNow,
    isSaving,
    isLoading,
  } = useWorkflowFieldValues({
    ontologyFields,
    sections,
    form,
  })

  const saveGuard = useRegisterSaveGuard({
    hasUnsavedEdits: !readonly && hasUnsavedEdits,
    hasUnsavedEditsOrSystemValues: !readonly && hasUnsavedEditsOrSystemValues,
    save: handleSave,
    canProceed,
  })
  const { onDiscardAndContinue } = saveGuard
  const handleDiscardAndContinue = useCallback(() => {
    if (isSavingNow()) return
    onDiscardAndContinue()
  }, [isSavingNow, onDiscardAndContinue])

  return (
    <Container>
      <SaveGuardModal
        {...saveGuard}
        isSaving={saveGuard.isSaving || isSaving}
        onDiscardAndContinue={handleDiscardAndContinue}
      />
      {!readonly && (
        <StickySaveBannerContainer>
          <NiiSaveBanner
            hasChanges={hasUnsavedEditsOrSystemValues}
            isLoading={isLoading || isSaving}
            onReset={handleCancel}
            onSave={handleSave}
          />
        </StickySaveBannerContainer>
      )}
      {sections.map((section) => (
        <SectionRenderer
          key={section.id}
          section={section}
          stepId={stepId}
          fieldValues={resolvedFieldValues}
          ontologyFields={ontologyFields}
          setFieldValues={setFieldValues}
          sections={sections}
          readonly={readonly}
        />
      ))}
    </Container>
  )
}

export const useValidateConfig: UseValidateConfigHook = ({ config }) => {
  return useMemo(() => {
    const result = niiScreeningConfigSchema.safeParse(config)
    if (!result.success) {
      console.error('NiiScreening config validation failed:', result.error)
      console.error('Config that failed validation:', config)
    }
    return result.success
  }, [config])
}

const NiiScreeningComponent: FC<
  LibraWorflowStepComponentProps<NiiScreeningConfig>
> = ({ config, readonly }) => {
  const parsed = useMemo(
    () => niiScreeningConfigSchema.safeParse(config),
    [config]
  )

  if (!parsed.success) {
    console.error(
      'NiiScreening Component: Failed to parse config:',
      parsed.error
    )
    console.error('Raw config:', config)

    return <div>Unable to parse NII config</div>
  }

  const niiConfig = parsed.data

  if (niiConfig.sections?.length) {
    return (
      <ConfigDrivenNiiScreeningContent config={niiConfig} readonly={readonly} />
    )
  }

  return (
    <NiiScreeningDataProvider niiConfig={niiConfig}>
      <LegacyNiiScreeningContent readonly={readonly} />
    </NiiScreeningDataProvider>
  )
}

NiiScreeningComponent.displayName = 'NiiScreeningComponent'

export const Component = React.memo(NiiScreeningComponent)
